import SalesChart from "./salesCharts/SalesCharts";
import InboxChart from "./charts/InboxChart";
import SentChart from "./charts/SentCharts";
import ResponseTime from "./charts/ResponseTime";
import RepeatCustomerChart from "./charts/RepeatCustomerChart";
import FirstResponse from "./charts/FirstResponse";
import SentinmentsCard from "./cards/SentinmentsCard";
import DispositionCards from "./cards/DispositionCards";
import EmailPieChart from "./charts/EmailPieChart";
import ActionableVsNonActionable from "./charts/ActionableVsNonActionable";
import { useState, useEffect } from "react";
import LoadingSpinner from "../../../components/LoadingSpinner";
import EmailCustomDate from "./EmailCustomDate";
import { useGetAllTeamsQuery } from "../../../Services/reports/tickets/ReportTickets";
import { useEmailDashboardQuery } from "../../../Services/dashboard/EmailDashboardApi";
import "../DashboardPageLayout.scss";

const TAB_TO_API: Record<string, string> = {
  overview: "overview",
  performance: "performance",
  analytics: "analytics",
  sentiments: "sentiments",
};

const EmailDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [theTeams, setTheTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState(
    localStorage.getItem("teamId") || "",
  );

  const { data: teamsData } = useGetAllTeamsQuery({});

  const [params, setParams] = useState<any>({
    team: localStorage.getItem("teamId") || "",
    start_date: "",
    end_date: "",
  });

  const [dateRange, setDateRange] = useState<any>([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const formatToDate = (date: Date): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ── Teams ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (teamsData) setTheTeams(teamsData?.teams || []);
  }, [teamsData]);

  // ── Sync team into params ─────────────────────────────────────────────────
  useEffect(() => {
    if (selectedTeam) {
      setParams((prev: any) => ({ ...prev, team: selectedTeam }));
    }
  }, [selectedTeam]);

  // ── Sync date range into params ───────────────────────────────────────────
  useEffect(() => {
    if (dateRange?.[0]) {
      setParams((prev: any) => ({
        ...prev,
        start_date: formatToDate(dateRange[0].startDate),
        end_date: formatToDate(dateRange[0].endDate),
      }));
    }
  }, [dateRange]);

  const canFetch = !!(params.team && params.start_date && params.end_date);

  // ── Single query — dashboardType switches per active tab ──────────────────
  const { data: tabData, isLoading } = useEmailDashboardQuery(
    {
      dashboardType: TAB_TO_API[activeTab],
      selectedTeam: params.team,
      graphStartDate: params.start_date,
      graphEndDate: params.end_date,
    },
    { skip: !canFetch },
  );

  // ── Shared click-to-expand props passed to every chart ────────────────────
  const clickToExpandProps = {
    startDate: params.start_date,
    endDate: params.end_date,
    teamId: params.team || null,
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "performance", label: "Performance", icon: "📈" },
    { id: "analytics", label: "Analytics", icon: "🔍" },
    { id: "sentiments", label: "Sentiments", icon: "💬" },
  ];

  // ── Tab content ───────────────────────────────────────────────────────────
  const renderTabContent = () => {
    // Match ticket dashboard: spinner only on first load so refetches (e.g. date
    // change from SalesChart fullscreen) keep children mounted and modal state intact.
    if (isLoading && tabData == null) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <LoadingSpinner />
        </div>
      );
    }

    switch (activeTab) {
      // ── Overview ──────────────────────────────────────────────────────────
      case "overview":
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <SalesChart
              emailDashboardData={tabData}
              {...clickToExpandProps}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
              }}
            >
              <InboxChart
                emailDashboardData={tabData}
                {...clickToExpandProps}
              />
              <SentChart emailDashboardData={tabData} {...clickToExpandProps} />
              <ResponseTime
                emailDashboardData={tabData}
                {...clickToExpandProps}
              />
              <RepeatCustomerChart
                emailDashboardData={tabData}
                {...clickToExpandProps}
              />
            </div>

            {/* ── Actionable vs Non-Actionable ── */}

          </div>
        );

      // ── Performance ───────────────────────────────────────────────────────
      case "performance":
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <div
                style={{
                  flex: "1.5 1 280px",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                  minHeight: "180px",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <FirstResponse
                  emailDashboardPerformance={tabData}
                  teamId={selectedTeam}
                  {...clickToExpandProps}
                />
              </div>

              <div
                style={{
                  flex: "1 1 280px",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                  minHeight: "180px",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <ActionableVsNonActionable
                  data={tabData}
                  {...clickToExpandProps}
                />
              </div>
            </div>
          </div>
        );

      // ── Analytics ─────────────────────────────────────────────────────────
      case "analytics":
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                gap: "20px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                }}
              >
                <DispositionCards
                  emailDashboardAnalyticsData={tabData}
                  {...clickToExpandProps}
                />
              </div>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                }}
              >
                <EmailPieChart
                  overview={tabData?.tickets_by_status_type}
                  {...clickToExpandProps}
                />
              </div>
            </div>
          </div>
        );

      // ── Sentiments ────────────────────────────────────────────────────────
      case "sentiments":
        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
                padding: "20px",
              }}
            >
              <SentinmentsCard title="Customer sentiments" />
            </div>
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
                padding: "20px",
              }}
            >
              <SentinmentsCard title="Word cloud" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-page">
      {/* Header */}
      <div
        className="dashboard-page__header"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e2e8f0",
          padding: "20px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Email dashboard</h2>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Monitor your email performance and analytics
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative" }}>
              <EmailCustomDate
                dateRange={dateRange}
                setDateRange={setDateRange}
              />
            </div>

            <div style={{ position: "relative", minWidth: "200px" }}>
              <label
                style={{
                  position: "absolute",
                  top: "-8px",
                  left: "12px",
                  backgroundColor: "#ffffff",
                  padding: "0 4px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#64748b",
                  zIndex: 1,
                }}
              >
                Team
              </label>
              <select
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#1e293b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                value={selectedTeam}
                onChange={(e: any) => setSelectedTeam(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366f1";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">Select team</option>
                {theTeams.map((team: any) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="tab-container dashboard-page__tabs"
        style={{
          display: "flex",
          gap: "24px",
          padding: "0 16px",
          width: "100%",
          overflowX: "auto",
          scrollbarWidth: "none",
          borderBottom: "1px solid #e0e0e0",
          position: "relative",
          height: "48px",
          alignItems: "center",
          backgroundColor: "#ffffff",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              style={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                borderBottom: isActive
                  ? "2px solid #6366f1"
                  : "2px solid transparent",
                marginBottom: "-1px",
                transition: "border-color 0.2s ease",
              }}
            >
              <button
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "12px 16px",
                  margin: 0,
                  border: "none",
                  backgroundColor: "transparent",
                  color: isActive ? "#6366f1" : "#1e293b",
                  cursor: "pointer",
                  fontWeight: isActive ? "600" : "400",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  outline: "none",
                  transition: "color 0.2s ease",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = "#6366f1";
                    (
                      e.currentTarget.closest("div") as HTMLElement
                    ).style.borderBottomColor = "#6366f1";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = "#1e293b";
                    (
                      e.currentTarget.closest("div") as HTMLElement
                    ).style.borderBottomColor = "transparent";
                  }
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>{tab.icon}</span>
                <span className="tab-button-text">{tab.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div className="dashboard-page__content">{renderTabContent()}</div>
    </div>
  );
};

export default EmailDashboard;
